import type { LoaderFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import { getRecentlyViewedServer } from '~/modules-rocco/recently-viewed/api/get-recently-viewed.server';
import { mapRecentlyViewedToTileViewModel } from '~/modules-rocco/recently-viewed/data-mappers/tile-generic';
import { mapProductToTileViewModel } from '@modules-rocco/products/data-mappers/tile-product';

export const RECENTLY_VIEWED_ACTION = '/actions/recently-viewed';

type RecentlyViewedType = 'Products' | 'All';

/**
 * API endpoint to get the user's most recently viewed items. Use with the
 * `useRecentlyViewed` hook.
 *
 * Query params:
 * - type: 'Products' | 'All' (default: 'All')
 *   - 'Products': Returns ProductTileViewModel[] in `recentlyViewedProducts`
 *   - 'All': Returns TileViewModel[] in `recentlyViewedItems`
 *
 * @see {@link packages/web/app/modules-rocco/recently-viewed/hooks/use-recently-viewed.ts}
 */
export const loader = async ({ request, context }: LoaderFunctionArgs) => {
    const { appDisplayMode, user, logger } = context;
    const url = new URL(request.url);
    const typeParam = url.searchParams.get('type');
    const type: RecentlyViewedType =
        typeParam === 'Products' ? 'Products' : 'All';

    try {
        const recentlyViewed = await getRecentlyViewedServer({ type }, context);

        // For Products type, return ProductTileViewModel[] for use in product pages
        if (type === 'Products') {
            const products =
                recentlyViewed.RecentlyViewedProducts?.map((product) =>
                    mapProductToTileViewModel(product, user, {
                        appDisplayMode,
                    }),
                ) ?? [];

            return {
                recentlyViewedProducts: products,
                recentlyViewedItems: null,
            };
        }

        // For All type, return generic tile view models
        const recentlyViewedTiles = recentlyViewed.RecentlyViewedAll.map(
            (item) =>
                mapRecentlyViewedToTileViewModel(item, user, appDisplayMode),
        );

        return {
            recentlyViewedItems: recentlyViewedTiles,
            recentlyViewedProducts: null,
        };
    } catch (error) {
        logger.error('Error in /actions/recently-viewed', { error, type });
        return routerData(null, { status: 500 });
    }
};
